/**
 * Maps a group-service post object to the PostCard feed shape.
 */
import type { FeedPost, Group, GroupPost } from '../types';
import { formatContentTimestamp } from './dateHelpers';

const isUsableMediaUri = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** Fill missing group icons from cached group lists (home Groups feed). */
export const enrichGroupPostsWithGroupImages = <T extends Pick<GroupPost, 'groupId' | 'groupImage'>>(
  posts: T[],
  groups: Group[],
): T[] => {
  if (groups.length === 0) return posts;

  const imageByGroupId = new Map(
    groups.map(group => [String(group.id), group.groupImage ?? null]),
  );

  return posts.map(post => {
    if (post.groupImage || !post.groupId) return post;
    const cached = imageByGroupId.get(String(post.groupId));
    return cached ? { ...post, groupImage: cached } : post;
  });
};

/** Same as above for mapped feed cards — keeps `avatar` in sync with `groupImage`. */
export const enrichGroupFeedPostsWithGroupImages = <T extends FeedPost>(
  posts: T[],
  groups: Group[],
): T[] => {
  if (groups.length === 0) return posts;

  const imageByGroupId = new Map(
    groups.map(group => [String(group.id), group.groupImage ?? null]),
  );

  return posts.map(post => {
    if (post.groupImage || !post.groupId) return post;
    const cached = imageByGroupId.get(String(post.groupId));
    if (!cached) return post;
    return { ...post, groupImage: cached, avatar: cached };
  });
};

export const normalizeGroupFeedPost = <T extends FeedPost>(post: T): T => ({
  ...post,
  likes: post.likes ?? 0,
  comments: post.comments ?? 0,
  shares: post.shares ?? 0,
  text: post.text ?? '',
  username: post.username ?? '',
  timeAgo: post.timeAgo ?? '',
  image:
    post.image == null
      ? null
      : typeof post.image === 'string'
        ? isUsableMediaUri(post.image)
          ? post.image
          : null
        : post.image,
  avatar:
    post.avatar == null
      ? undefined
      : typeof post.avatar === 'string'
        ? isUsableMediaUri(post.avatar)
          ? post.avatar
          : undefined
        : post.avatar,
});

export const mapGroupPostToFeed = (gp: GroupPost): FeedPost =>
  normalizeGroupFeedPost({
  id: gp.id,
  groupId: gp.groupId,
  profileUserGuid: gp.profileUserGuid,
  username: gp.userName || gp.groupName || '',
  groupName: gp.groupName,
  groupImage: gp.groupImage ?? null,
  userProfileImage: gp.userProfileImage ?? null,
  groupUserName: gp.userName,
  avatar: gp.groupImage,
  text: gp.postDescription,
  feeling: gp.feeling ?? null,
  image: gp.postImage,
  images: gp.images,
  imageLayout: gp.imageLayout,
  video: gp.postVideo
    ? { uri: typeof gp.postVideo === 'string' ? gp.postVideo : String(gp.postVideo) }
    : null,
  timeAgo: formatContentTimestamp(gp.postDate || gp.createdAt, 'local') || gp.createdAt || '',
  postDate: gp.postDate,
  likes: gp.likeCount,
  comments: gp.commentCount,
  shares: gp.shareCount,
  liked: gp.liked,
  saved: gp.saved,
  currentWeight: gp.currentWeight ?? '',
  userId: gp.userId,
  firstName: gp.firstName,
  isGroupPost: true,
  allowShare: gp.allowShare,
  });

export const mapGroupPostsToFeed = (posts: GroupPost[]): FeedPost[] =>
  posts.map(gp =>
    normalizeGroupFeedPost({
      ...mapGroupPostToFeed(gp),
      username: gp.userName || gp.groupName || '',
      firstName: gp.firstName,
      currentWeight: gp.currentWeight ?? '',
      avatar: gp.userProfileImage ?? gp.groupImage ?? undefined,
      groupImage: gp.groupImage ?? null,
      userProfileImage: gp.userProfileImage ?? null,
      isGroupPost: true,
    }),
  );
